<script setup>
import { defineProps, defineEmits, toRef, watch } from "vue";
import { useDataTable } from "@/composables/table/useDataTable.js"; // Adjust path as needed
import { ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon } from "lucide-vue-next";
// Import your UI components
import Checkbox from "@/components/ui/checkbox/Checkbox.vue"; // Placeholder
import Button from "@/components/ui/button/Button.vue"; // Placeholder
import Input from "@/components/ui/input/Input.vue"; // Placeholder

// 1. Define ALL props for the component, including selection props
const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  fields: {
    type: Array,
    default: () => [],
  },
  orderBy: {
    type: String,
    default: null,
  },
  orderDirection: {
    type: String,
    default: "asc",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingRowCount: {
    type: Number,
    default: 10,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  rowClass: {
    type: Function,
    default: () => "",
  },
  // Add the selection props
  selectable: {
    type: Boolean,
    default: false,
  },
  selectedRows: {
    type: Array,
    default: () => [],
  },
  rowKey: {
    type: String,
    default: "id",
  },
});

// 2. Define ALL emits, including the selection emit
const emit = defineEmits([
  "sort-changed",
  "filters-changed",
  "update:selectedRows",
]);

// 3. Instantiate the composable
// We pass all the prop Refs and the emit function it needs.
const {
  fieldsSearchable,
  sortChanged,
  filterChanged,
  rowFieldValue,
  toggleRowSelection,
  isRowSelected,
  // selectAll, // Available if you add a "select all" checkbox
  // clearSelection, // Available if you need it
} = useDataTable({
  rows: toRef(props, "rows"),
  fields: toRef(props, "fields"),
  orderBy: toRef(props, "orderBy"),
  orderDirection: toRef(props, "orderDirection"),
  filters: toRef(props, "filters"),
  selectable: toRef(props, "selectable"),
  selectedRows: toRef(props, "selectedRows"),
  emit,
});

const nextSortDirection = (fieldName) => {
  if (props.orderBy !== fieldName) {
    return "asc";
  }

  return props.orderDirection === "asc" ? "desc" : "asc";
};

const getRowIdentifier = (row, rowIndex) => {
  const rowIdentifier = row?.[props.rowKey];

  if (rowIdentifier === undefined || rowIdentifier === null) {
    return rowIndex;
  }

  return rowIdentifier;
};

const getFilterValue = (fieldName) => {
  return props.filters?.[fieldName] ?? "";
};

let cellValueCache = new WeakMap();

watch([() => props.rows, () => props.fields], () => {
  cellValueCache = new WeakMap();
});

const getCellValue = (row, field) => {
  if (!row || typeof row !== "object" || !field || typeof field !== "object") {
    return rowFieldValue(row, field?.name, field?.fallback, field?.formatter);
  }

  let rowCache = cellValueCache.get(row);

  if (!rowCache) {
    rowCache = new WeakMap();
    cellValueCache.set(row, rowCache);
  }

  if (rowCache.has(field)) {
    return rowCache.get(field);
  }

  const value = rowFieldValue(row, field.name, field.fallback, field.formatter);
  rowCache.set(field, value);

  return value;
};

const resolveFieldComponentProps = (field, row, rowIndex) => {
  const defaultProps = {
    row,
    field,
    value: getCellValue(row, field),
    index: rowIndex,
  };

  if (typeof field.resolveProps !== "function") {
    return defaultProps;
  }

  let resolvedProps;

  try {
    resolvedProps = field.resolveProps(row, field, rowIndex);
  } catch (error) {
    console.error("Failed to resolve table cell props.", error);
    return defaultProps;
  }

  if (
    !resolvedProps ||
    typeof resolvedProps !== "object" ||
    Array.isArray(resolvedProps)
  ) {
    return defaultProps;
  }

  return {
    ...defaultProps,
    ...resolvedProps,
  };
};

// The script block is now done. All logic is in the composable.
// The template below will work as-is because we've returned
// all the same-named functions and computed properties.
</script>

<template>
  <table class="min-w-full divide-y divide-gray-300">
    <thead class="whitespace-nowrap">
      <tr>
        <th v-if="selectable">
&nbsp;
        </th>
        <th
          v-for="(field, index) in fields"
          :key="'table-column-key-' + (field.name ?? index)"
          scope="col"
          :class="[
            'p-3 text-left text-xs font-medium tracking-wide text-gray-500',
          ]"
        >
          <Button
            v-if="field.sortable"
            variant="link"
            :class="[
              { 'font-extrabold text-gray-900': field.name === orderBy },
              'font-lato text-xs',
            ]"
            @click="sortChanged(field.name, nextSortDirection(field.name))"
          >
            {{ field.label ? field.label : field.name }}
            <ArrowDownIcon
              v-if="orderBy === field.name && orderDirection === 'desc'"
            />
            <ArrowUpIcon
              v-else-if="orderBy === field.name && orderDirection === 'asc'"
            />
            <ArrowUpDownIcon v-else />
          </Button>
          <template v-else>
            {{ field.label ? field.label : field.name }}
          </template>
        </th>
      </tr>
      <tr v-if="fieldsSearchable.length > 0">
        <th v-if="selectable">
&nbsp;
        </th>
        <th
          v-for="(field, index) in fields"
          :key="'table-column-filter-key-' + (field.name ?? index)"
          scope="col"
          class="text-left text-sm font-forms text-gray-400 px-3"
        >
          <Input
            v-if="field.searchable"
            :ref="'field' + index"
            class="mb-3"
            size="xs"
            :model-value="getFilterValue(field.name)"
            :placeholder="
              'Search in ' + (field.label ? field.label : field.name) + '...'
            "
            @update:model-value="filterChanged(field.name, $event)"
          />
        </th>
      </tr>
    </thead>
    <tbody class="divide-y divide-gray-200">
      <template v-if="loading">
        <tr v-for="n in loadingRowCount" :key="n">
          <td v-if="selectable" />
          <td v-for="(field, index) in fields" :key="index" class="p-2">
            <div class="animate-pulse bg-gray-200 h-4 rounded" />
          </td>
        </tr>
      </template>
      <template v-else>
        <tr
          v-for="(row, rowIndex) in rows"
          :key="'table-row-key-' + getRowIdentifier(row, rowIndex)"
          :class="[
            'hover:bg-gray-100',
            rowClass(row),
            { 'bg-gray-200': isRowSelected(row, rowKey) },
          ]"
        >
          <td v-if="selectable">
            <Checkbox
              class="m-2"
              :model-value="isRowSelected(row, rowKey)"
              @change="toggleRowSelection(row, rowKey)"
            />
          </td>
          <td
            v-for="(field, fieldIndex) in fields"
            :key="
              'table-row-field-key-' +
                '-' +
                getRowIdentifier(row, rowIndex) +
                '-' +
                (field.name ?? fieldIndex)
            "
            class="min-w-20 py-5 px-3 font-forms text-sm text-gray-900"
          >
            <slot
              :name="'cell(' + field.name + ')'"
              :row="row"
              :field="field"
              :value="getCellValue(row, field)"
              :index="rowIndex"
            >
              <component
                :is="field.component"
                v-if="field.component"
                v-bind="resolveFieldComponentProps(field, row, rowIndex)"
              />
              <template v-else>
                {{ getCellValue(row, field) }}
              </template>
            </slot>
          </td>
        </tr>
      </template>
      <tr v-if="!rows.length && !loading">
        <td
          :colspan="fields.length + (selectable ? 1 : 0)"
          class="text-center text-gray-500 pt-10 py-8"
        >
          <slot name="noData">
            No data available.
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>
